/*
 * Browser audio for the core API: audio.devices / audio.select, the
 * microphone meter (diag.mic_start / diag.mic_stop), the speaker / ring
 * device tone (diag.tone), the ringtone (audio.ring) and the echo-test call
 * (diag.call_test). Device labels need a microphone permission first; the
 * browser gives empty names until then.
 */
import type { Core, CoreModule } from "./core";
import type { SipJsStack } from "./sipjs";
import { callStats, mosEstimate } from "./stats";
import { CommandError, Command, Json } from "./types";

type Sink = HTMLAudioElement & { setSinkId?(id: string): Promise<void> };

interface Device extends Json {
  driver: "browser";
  name: string;
  id: string;
  default: boolean;
}

export class MediaModule implements CoreModule {
  private alertId?: string;
  private meter?: { stream: MediaStream; ctx: AudioContext; timer: ReturnType<typeof setInterval> };
  private ring?: Sink;

  constructor(
    private core: Core,
    private stack: SipJsStack,
    private ringtoneUrl = "/ringtone.wav",
  ) {}

  command(c: Command): Promise<Json | void> | undefined {
    switch (c.cmd) {
      case "audio.echo_cancellation":
        this.stack.media.echoCancellation = c.enable !== false;
        return Promise.resolve();
      case "audio.devices":
        return this.devices();
      case "audio.select":
        return this.select(c);
      case "diag.mic_start":
        return this.micStart();
      case "diag.mic_stop":
        return Promise.resolve(this.micStop());
      case "diag.tone":
        return this.tone(c.target === "alert" ? this.alertId : this.stack.media.outputDeviceId, Number(c.seconds ?? 2));
      case "audio.ring":
        return Promise.resolve(c.stop === true ? this.ringStop() : this.ringStart());
      case "diag.call_test":
        return this.callTest(String(c.account ?? ""), String(c.to ?? ""), Number(c.seconds ?? 10));
      default:
        return undefined;
    }
  }

  stop(): void {
    this.micStop();
    this.ringStop();
  }

  private async devices(): Promise<Json> {
    const all = await navigator.mediaDevices.enumerateDevices();
    const map = (kind: MediaDeviceKind): Device[] =>
      all
        .filter((d) => d.kind === kind)
        .map((d) => ({ driver: "browser", name: d.label || d.deviceId, id: d.deviceId, default: d.deviceId === "default" }));
    return {
      sources: map("audioinput"),
      players: map("audiooutput"),
      // setSinkId is missing in some browsers: the UI then hides the choice
      can_select_player: typeof (HTMLMediaElement.prototype as Sink).setSinkId === "function",
      selected: {
        source: this.stack.media.inputDeviceId ?? "",
        player: this.stack.media.outputDeviceId ?? "",
        alert: this.alertId ?? "",
      },
    };
  }

  private async select(c: Command): Promise<void> {
    const id = (v: unknown) => {
      if (v === null || v === "") return undefined;
      const o = v as { id?: string; name?: string };
      if (typeof o !== "object" || typeof (o.id ?? o.name) !== "string") throw new CommandError("invalid");
      return o.id ?? o.name;
    };
    if (c.source !== undefined) await this.stack.setInputDevice(id(c.source));
    if (c.player !== undefined) await this.stack.setOutputDevice(id(c.player));
    if (c.alert !== undefined) {
      this.alertId = id(c.alert);
      if (this.ring) await this.ring.setSinkId?.(this.alertId ?? "").catch(() => undefined);
    }
  }

  private async micStart(): Promise<void> {
    this.micStop();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(this.stack.constraints());
    } catch (e) {
      this.core.event("diag.mic_level", { error: (e as Error).name || "unavailable" });
      throw new CommandError("unavailable", String(e));
    }
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const db = (x: number) => Math.round(Math.max(-90, 20 * Math.log10(Math.max(x, 1e-9))) * 10) / 10;
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      let peak = 0;
      for (const v of buf) {
        sum += v * v;
        peak = Math.max(peak, Math.abs(v));
      }
      this.core.event("diag.mic_level", { rms_db: db(Math.sqrt(sum / buf.length)), peak_db: db(peak) });
    }, 100);
    this.meter = { stream, ctx, timer };
  }

  private micStop(): void {
    if (!this.meter) return;
    clearInterval(this.meter.timer);
    this.meter.stream.getTracks().forEach((t) => t.stop());
    void this.meter.ctx.close();
    this.meter = undefined;
  }

  /** 1 kHz at -12 dBFS on a device, through an <audio> element so setSinkId applies. */
  private async tone(sinkId: string | undefined, seconds: number): Promise<void> {
    const s = Math.min(10, Math.max(0.5, seconds || 2));
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const dest = ctx.createMediaStreamDestination();
    osc.frequency.value = 1000;
    gain.gain.value = 0.25;
    osc.connect(gain).connect(dest);
    const el = document.createElement("audio") as Sink;
    if (sinkId && el.setSinkId) await el.setSinkId(sinkId).catch(() => undefined);
    el.srcObject = dest.stream;
    osc.start();
    await el.play().catch(() => undefined);
    setTimeout(() => {
      osc.stop();
      el.srcObject = null;
      void ctx.close();
      this.core.event("diag.tone", { done: true });
    }, s * 1000);
  }

  private ringStart(): void {
    if (this.ring) return;
    const el = new Audio(this.ringtoneUrl) as Sink;
    el.loop = true;
    if (this.alertId && el.setSinkId) void el.setSinkId(this.alertId).catch(() => undefined);
    // autoplay policy: a page nobody interacted with may refuse; the
    // notification still shows
    void el.play().catch(() => this.core.log("warn", "ringtone blocked by the browser's autoplay policy"));
    this.ring = el;
  }

  private ringStop(): void {
    this.ring?.pause();
    this.ring = undefined;
  }

  /** Calls the echo service, measures for `seconds`, hangs up. */
  private async callTest(account: string, to: string, seconds: number): Promise<Json> {
    const s = Math.min(60, Math.max(3, seconds || 10));
    const r = await this.core.command({ cmd: "call.dial", account, to });
    if (!r.ok) throw new CommandError(String(r.error), String(r.message ?? ""));
    const id = (r.data as { call: string }).call;
    const deadline = Date.now() + 15_000;
    while (this.core.callRec(id)?.state !== "established") {
      const rec = this.core.callRec(id);
      if (!rec || rec.state === "ended" || Date.now() > deadline) {
        if (rec) await this.core.command({ cmd: "call.hangup", call: id });
        throw new CommandError("call_failed", "the test call was not answered");
      }
      await sleep(200);
    }
    await sleep(s * 1000);
    const pc = this.core.callRec(id)?.stack.peerConnection();
    const st = pc ? await callStats(`test-${id}`, pc) : undefined;
    await this.core.command({ cmd: "call.hangup", call: id });
    if (!st || st.rx_packets === 0) throw new CommandError("call_failed", "no audio received");
    const m = mosEstimate({ rtt_ms: st.rtt_ms, jitter_ms: st.rx_jitter_ms, loss_pct: st.loss_pct, codec: st.codec });
    return {
      codec: st.codec,
      rx_packets: st.rx_packets,
      rtt_ms: st.rtt_ms,
      loss_pct: st.loss_pct,
      jitter_ms: st.rx_jitter_ms,
      ...m,
    };
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
